import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../../lib/api';

const styles = {
  bg: '#020202',
  s1: '#0d0d0d',
  s2: '#141414',
  bd: 'rgba(255,255,255,0.08)',
  bd2: 'rgba(255,255,255,0.13)',
  bd3: 'rgba(255,255,255,0.22)',
  tx: '#f2f2f2',
  t2: '#c0c0c0',
  t3: '#888',
  t4: '#555',
  ac: '#fff',
  em: '#10b981',
};

const TOTAL_STEPS = 3;

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // ignore
    }
  };
  return (
    <button
      onClick={handleCopy}
      style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 10,
        border: `1px solid ${styles.bd}`,
        borderRadius: 3,
        padding: '2px 9px',
        background: 'none',
        color: styles.t4,
        cursor: 'pointer',
      }}
    >
      {copied ? 'copied' : 'copy'}
    </button>
  );
}

function CodeBlock({ filename, code }) {
  return (
    <div style={{
      background: styles.s1,
      border: `1px solid ${styles.bd}`,
      borderRadius: 6,
      overflow: 'hidden',
      marginBottom: 12,
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 13px',
        borderBottom: `1px solid ${styles.bd}`,
      }}>
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10,
          color: styles.t4,
          letterSpacing: '0.04em',
        }}>
          {filename}
        </span>
        <CopyButton text={code} />
      </div>
      <pre style={{
        padding: '13px 15px',
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 12,
        lineHeight: 1.85,
        color: styles.t2,
        overflowX: 'auto',
        whiteSpace: 'pre',
        margin: 0,
      }}>
        {code}
      </pre>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <span style={{
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 9.5,
      textTransform: 'uppercase',
      letterSpacing: '0.09em',
      color: styles.t4,
      marginBottom: 8,
      display: 'block',
    }}>
      {children}
    </span>
  );
}

function Overline({ children }) {
  return (
    <span style={{
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 10,
      fontWeight: 500,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: styles.t4,
      marginBottom: 14,
      display: 'block',
    }}>
      {children}
    </span>
  );
}

function H1({ children }) {
  return (
    <h1 style={{
      fontSize: 26,
      fontWeight: 600,
      letterSpacing: '-0.5px',
      lineHeight: 1.2,
      color: styles.ac,
      margin: 0,
      marginBottom: 10,
    }}>
      {children}
    </h1>
  );
}

function Subtitle({ children }) {
  return (
    <p style={{
      fontSize: 14,
      color: styles.t2,
      lineHeight: 1.7,
      maxWidth: 540,
      margin: 0,
      marginBottom: 28,
    }}>
      {children}
    </p>
  );
}

function StepInstall({ tenantId, apiKey, loading }) {
  const envCode = `KALIBR_API_KEY=${apiKey || 'sk_...'}
KALIBR_TENANT_ID=${tenantId || 'tenant_...'}
OPENAI_API_KEY=sk-...`;

  return (
    <div style={{ width: '100%', maxWidth: 720 }}>
      <Overline>Step 01 — Install</Overline>
      <H1>Install Kalibr</H1>
      <Subtitle>
        Install the SDK for your language, then set your credentials as environment variables.
      </Subtitle>

      <SectionLabel>SDK</SectionLabel>
      <CodeBlock filename="terminal" code={'pip install kalibr'} />
      <CodeBlock filename="terminal" code={'npm install @kalibr/sdk'} />

      <SectionLabel>Environment</SectionLabel>
      {loading ? (
        <div style={{
          padding: '12px 14px',
          background: styles.s1,
          border: `1px solid ${styles.bd}`,
          borderRadius: 6,
          color: styles.t4,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 12,
        }}>
          Loading credentials…
        </div>
      ) : (
        <CodeBlock filename=".env" code={envCode} />
      )}

      <div style={{
        marginTop: 6,
        fontSize: 12,
        color: styles.t3,
        lineHeight: 1.6,
      }}>
        Your tenant ID and a fresh API key are populated above. Copy this key now — it
        will not be shown in full again. You can rotate keys anytime in Settings.
      </div>
    </div>
  );
}

function StepAddToCode() {
  const [tab, setTab] = useState('Python');

  const PY_CODE = `import kalibr  # must be first import
from kalibr import Router

router = Router(
    goal="your_task",
    paths=["gpt-4o", "deepseek-chat"],
    success_when=lambda out: len(out) > 0,
)
response = router.completion(messages=[...])`;

  const TS_CODE = `import "@kalibr/sdk/register"; // must be first import
import { Router } from "@kalibr/sdk";

const router = new Router({
  goal: "your_task",
  paths: ["gpt-4o", "deepseek-chat"],
  successWhen: (out) => out.length > 0,
});

const response = await router.completion({ messages: [/* ... */] });`;

  const code = tab === 'Python' ? PY_CODE : TS_CODE;
  const filename = tab === 'Python' ? 'agent.py' : 'agent.ts';

  return (
    <div style={{ width: '100%', maxWidth: 720 }}>
      <Overline>Step 02 — Integrate</Overline>
      <H1>Add Kalibr to your agent</H1>
      <Subtitle>
        Wrap the model calls in your agent with a Router. Kalibr picks the best path,
        records the outcome, and learns over time.
      </Subtitle>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {['Python', 'TypeScript'].map((lang) => (
          <button
            key={lang}
            onClick={() => setTab(lang)}
            style={{
              padding: '5px 13px',
              background: tab === lang ? styles.s2 : styles.s1,
              border: `1px solid ${tab === lang ? styles.bd3 : styles.bd}`,
              borderRadius: 999,
              fontSize: 12.5,
              fontWeight: 500,
              color: tab === lang ? styles.tx : styles.t3,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {lang}
          </button>
        ))}
      </div>

      <CodeBlock filename={filename} code={code} />

      <div style={{
        marginTop: 6,
        fontSize: 12,
        color: styles.t3,
        lineHeight: 1.6,
      }}>
        That is the full integration. No code changes to your model providers, no
        proxy to run. Kalibr instruments your SDK and the Router handles selection.
      </div>
    </div>
  );
}

function StepVerify({ onComplete }) {
  const [traceCount, setTraceCount] = useState(0);
  const [checking, setChecking] = useState(false);
  const [verified, setVerified] = useState(false);
  const intervalRef = useRef(null);

  const checkTraces = async () => {
    setChecking(true);
    try {
      const data = await apiClient.getTraces({ hours: 1, limit: 10 });
      const traces = Array.isArray(data) ? data : data?.traces || [];
      if (traces.length > 0) {
        setTraceCount(traces.length);
        setVerified(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    } catch (err) {
      // silently ignore polling errors
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    checkTraces();
    intervalRef.current = setInterval(checkTraces, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <div style={{ width: '100%', maxWidth: 580, textAlign: 'center' }}>
      <div style={{
        width: 52,
        height: 52,
        borderRadius: '50%',
        border: `1px solid ${verified ? 'rgba(16,185,129,0.25)' : styles.bd2}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 22px',
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 16,
        color: verified ? styles.em : styles.t3,
      }}>
        {verified ? '✓' : '○'}
      </div>

      <Overline>Step 03 — Verify</Overline>
      <H1>{verified ? 'You are live' : 'Verify your connection'}</H1>
      <p style={{
        fontSize: 14,
        color: styles.t2,
        lineHeight: 1.7,
        maxWidth: 480,
        margin: '0 auto 24px',
      }}>
        {verified
          ? `${traceCount} routing decision${traceCount === 1 ? '' : 's'} received. Your Router is connected to Kalibr.`
          : 'Make a call using your Router. Your first routing decision will appear here.'}
      </p>

      {!verified && (
        <>
          <div style={{ maxWidth: 440, margin: '0 auto 18px', textAlign: 'left' }}>
            <CodeBlock
              filename="run"
              code={'response = router.completion(messages=[{"role": "user", "content": "ping"}])'}
            />
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            fontSize: 12,
            color: styles.t3,
            fontFamily: "'JetBrains Mono', monospace",
            marginBottom: 18,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: '#f59e0b',
              animation: 'pulse 1.6s ease-in-out infinite',
            }} />
            Waiting for your first call…
            <style>{`@keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.3 } }`}</style>
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        <button
          onClick={checkTraces}
          disabled={checking}
          style={{
            height: 32,
            padding: '0 16px',
            borderRadius: 5,
            fontSize: 13,
            fontWeight: 500,
            cursor: checking ? 'wait' : 'pointer',
            background: 'transparent',
            color: styles.t2,
            border: `1px solid ${styles.bd2}`,
            fontFamily: 'inherit',
            opacity: checking ? 0.6 : 1,
          }}
        >
          {checking ? 'Checking…' : 'Check connection'}
        </button>
        {verified && (
          <button
            onClick={onComplete}
            style={{
              height: 32,
              padding: '0 16px',
              borderRadius: 5,
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              background: styles.ac,
              color: '#000',
              border: 'none',
              fontFamily: 'inherit',
            }}
          >
            Continue to dashboard
          </button>
        )}
      </div>
    </div>
  );
}

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [tenantId, setTenantId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [credsLoading, setCredsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiClient.getCredentials()
      .then((data) => {
        if (cancelled) return;
        setTenantId(data?.tenant_id || '');
        if (data?.api_key) setApiKey(data.api_key);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setCredsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleComplete = () => {
    localStorage.setItem('kalibr_onboarding_complete', 'true');
    window.location.href = '/';
  };

  const handleContinue = () => {
    if (step < TOTAL_STEPS) setStep(step + 1);
    else handleComplete();
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const progress = (step / TOTAL_STEPS) * 100;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: styles.bg,
      fontFamily: "'Inter', -apple-system, sans-serif",
      color: styles.tx,
      fontSize: 14,
      lineHeight: 1.6,
    }}>
      {/* Top bar */}
      <div style={{
        height: 52,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 40px',
        borderBottom: `1px solid ${styles.bd}`,
        flexShrink: 0,
      }}>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: styles.ac,
        }}>
          Kalibr
        </div>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11,
          color: styles.t4,
        }}>
          {`0${step} / 0${TOTAL_STEPS}`}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height: 1, background: styles.bd, flexShrink: 0 }}>
        <div style={{
          height: '100%',
          background: 'rgba(255,255,255,0.3)',
          transition: 'width 0.3s ease',
          width: `${progress}%`,
        }} />
      </div>

      {/* Body */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '60px 24px',
        overflowY: 'auto',
      }}>
        {step === 1 && (
          <StepInstall tenantId={tenantId} apiKey={apiKey} loading={credsLoading} />
        )}
        {step === 2 && <StepAddToCode />}
        {step === 3 && <StepVerify onComplete={handleComplete} />}
      </div>

      {/* Footer */}
      <div style={{
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 40px',
        borderTop: `1px solid ${styles.bd}`,
        flexShrink: 0,
      }}>
        <div>
          {step !== 1 && (
            <button
              onClick={handleBack}
              style={{
                height: 32,
                padding: '0 16px',
                borderRadius: 5,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                background: 'transparent',
                color: styles.t2,
                border: `1px solid ${styles.bd2}`,
                fontFamily: 'inherit',
              }}
            >
              Back
            </button>
          )}
        </div>
        <div>
          {step < TOTAL_STEPS && (
            <button
              onClick={handleContinue}
              style={{
                height: 32,
                padding: '0 16px',
                borderRadius: 5,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                background: styles.ac,
                color: '#000',
                border: 'none',
                fontFamily: 'inherit',
              }}
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
